import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Zap, ArrowRight, Pencil } from "lucide-react";
import { waLink } from "../data/site";
import { Overline, SectionTitle, Accent, Reveal } from "./Reveal";

const DISCOUNT = 0.125;
const fmt = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const Simulator = () => {
  const [bill, setBill] = useState(438.6);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const savings = useMemo(() => bill * DISCOUNT, [bill]);
  const newBill = bill - savings;
  const yearly = savings * 12;
  const pct = ((bill - 150) / (3000 - 150)) * 100;

  const commitDraft = () => {
    const n = parseFloat(draft.replace(/[^\d,.]/g, "").replace(",", "."));
    if (!isNaN(n)) setBill(Math.min(3000, Math.max(150, n)));
    setEditing(false);
  };

  return (
    <section id="simular" className="relative py-24 sm:py-32 bg-[#061A14] overflow-hidden" data-testid="simulator-section">
      <div className="absolute top-0 right-0 w-[480px] h-[480px] rounded-full bg-[#00E676]/8 blur-[130px]" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <Overline>Simulador de desconto iGreen</Overline>
          <SectionTitle>
            Simule o seu <Accent>desconto</Accent>.
          </SectionTitle>
          <p className="mt-4 text-sm sm:text-base text-emerald-100/60">
            <strong className="text-white">+800 mil clientes</strong> economizando todo mês de forma sustentável
          </p>

          <div className="mt-10 rounded-2xl border border-[#00E676]/20 bg-[#0B261E]/70 backdrop-blur-md p-7 sm:p-9">
            <p className="text-sm font-semibold text-white mb-1.5">Quanto você paga de luz por mês?</p>
            {editing ? (
              <input
                autoFocus
                data-testid="simulator-bill-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commitDraft}
                onKeyDown={(e) => e.key === "Enter" && commitDraft()}
                className="font-heading text-4xl font-extrabold text-[#00E676] bg-transparent border-b-2 border-[#00E676]/50 outline-none w-56"
              />
            ) : (
              <button
                data-testid="simulator-bill-display"
                onClick={() => { setDraft(bill.toFixed(2).replace(".", ",")); setEditing(true); }}
                className="group inline-flex items-baseline gap-2 font-heading text-4xl sm:text-5xl font-extrabold text-[#00E676]"
              >
                {fmt(bill)}
                <span className="text-sm font-normal text-emerald-100/50 inline-flex items-center gap-1">
                  por mês <Pencil size={13} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
              </button>
            )}
            <input
              type="range"
              min="150"
              max="3000"
              step="10"
              value={bill}
              onChange={(e) => setBill(parseFloat(e.target.value))}
              className="savings-range mt-7"
              style={{ "--fill": `${pct}%` }}
              data-testid="savings-simulator-slider"
              aria-label="Valor da conta de luz por mês"
            />
            <div className="flex justify-between text-[11px] font-mono2 text-emerald-100/40 mt-2">
              <span>R$ 150</span><span>R$ 3.000+</span>
            </div>

            <div className="mt-8 border-t border-white/10 pt-7">
              <p className="text-sm text-emerald-100/70">
                Você economiza <span className="inline-flex items-center gap-1 text-[#00E676] font-semibold"><Zap size={14} /> 12,5% de desconto</span>
              </p>
              <motion.p
                key={savings.toFixed(0)}
                initial={{ scale: 0.96, opacity: 0.6 }}
                animate={{ scale: 1, opacity: 1 }}
                className="font-heading text-4xl sm:text-5xl font-extrabold text-white mt-2"
                data-testid="simulator-monthly-savings"
              >
                {fmt(savings)} <span className="text-base font-medium text-emerald-100/50">por mês</span>
              </motion.p>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <div className="rounded-xl bg-[#04120E]/70 border border-white/10 p-4">
                  <p className="text-xs text-emerald-100/50">Sua conta passa a ser</p>
                  <p className="font-heading text-xl font-bold text-white mt-1" data-testid="simulator-new-bill">{fmt(newBill)}<span className="text-xs text-emerald-100/50 font-normal">/mês</span></p>
                </div>
                <div className="rounded-xl bg-[#00E676]/10 border border-[#00E676]/25 p-4">
                  <p className="text-xs text-emerald-100/50">Economia em 1 ano</p>
                  <p className="font-heading text-xl font-bold text-[#00E676] mt-1" data-testid="simulator-yearly-savings">{fmt(yearly)}</p>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-emerald-100/55">
                {["Sem obras e instalações", "Sem investimento", "100% digital"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00E676]" /> {t}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <p className="mt-5 text-[11px] leading-relaxed text-emerald-100/40 max-w-lg">
            Simulação com 12,5% de desconto. O desconto vai até 15%, conforme a região, e vale sobre a energia compensada:
            taxa mínima, iluminação pública e impostos não entram. O valor exato sai na sua conta, depois da leitura do consumo.
          </p>
          <a
            href="#conectar"
            data-testid="simulator-guarantee-cta"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C853] text-[#04120E] font-bold px-7 py-4 shadow-[0_0_24px_rgba(0,230,118,0.4)] transition-[background-color,transform] duration-300 hover:scale-[1.03]"
          >
            Garantir meu desconto <ArrowRight size={17} />
          </a>
        </Reveal>

        <Reveal delay={0.15}>
          <InvoiceMockup bill={bill} savings={savings} newBill={newBill} />
        </Reveal>
      </div>
    </section>
  );
};

const InvoiceMockup = ({ bill, savings, newBill }) => (
  <div className="relative" data-testid="invoice-mockup">
    <div className="absolute -inset-4 rounded-3xl bg-[#00E676]/10 blur-2xl" />
    <div className="relative rounded-2xl bg-white text-slate-800 shadow-2xl overflow-hidden rotate-[1.2deg] transition-transform duration-500 hover:rotate-0">
      <div className="bg-[#0B261E] px-6 py-4 flex items-center justify-between">
        <p className="font-heading font-bold text-white text-sm">Fatura de Energia <span className="text-[#00E676]">iGreen</span></p>
        <span className="text-[10px] font-mono2 uppercase tracking-widest text-emerald-200/60 border border-[#00E676]/30 rounded-full px-2.5 py-1">Ilustrativo</span>
      </div>
      <div className="px-6 py-5 space-y-4 text-[13px]">
        <div className="flex justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400">Beneficiário</p>
            <p className="font-semibold">00.000.000/0001-00 · Usina Solar Parceira</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-slate-400">Referência</p>
            <p className="font-semibold">set/26</p>
          </div>
        </div>
        <div className="border-t border-dashed border-slate-200 pt-4">
          <p className="text-[10px] uppercase tracking-wider text-slate-400">Cliente</p>
          <p className="font-semibold">Maria Exemplo da Silva · CPF 000.000.000-00</p>
          <p className="text-slate-500 text-xs">Rua das Flores, 123 · Centro · Sua Cidade/SP</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-400">Energia verde compensada</p>
            <p className="font-heading font-bold text-base">452 kWh</p>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-400">Sem iGreen você pagaria</p>
            <p className="font-heading font-bold text-base line-through text-slate-400">{fmt(bill)}</p>
          </div>
        </div>
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-emerald-600">Total a pagar com desconto iGreen</p>
            <p className="font-heading text-2xl font-extrabold text-emerald-700">{fmt(newBill)}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-emerald-600">Desconto iGreen</p>
            <p className="font-heading text-xl font-extrabold text-emerald-600">{fmt(savings)}</p>
            <p className="text-[10px] font-mono2 text-emerald-500">12,5%</p>
          </div>
        </div>
        <p className="text-center text-xs text-emerald-700 font-medium">
          <strong>Parabéns!</strong> O planeta agradece a sua escolha.
        </p>
        <div className="flex items-center justify-between text-[11px] text-slate-400 border-t border-slate-100 pt-3">
          <span><strong className="text-slate-600">iGreen Club</strong> · +600 mil ofertas</span>
          <span>Vencimento <strong className="text-slate-600">10/10/2026</strong></span>
        </div>
      </div>
    </div>
    <p className="relative mt-4 text-[11px] text-emerald-100/40 leading-relaxed max-w-md">
      Fatura ilustrativa, com nomes e números fictícios. O desconto vai até 15%, conforme a região. Na prática chegam
      duas faturas, a da distribuidora e a da iGreen, que somadas saem mais baratas do que a conta de antes.
    </p>
  </div>
);
